import React, { useCallback, useMemo } from 'react';
import { View } from 'react-native';
import Animated, { useAnimatedScrollHandler, } from 'react-native-reanimated';
import { Button, CardView, Separator, rem } from 'design-system';
import SectionHeader from 'design-system/SectionHeader';
import Amount from 'design-system/Amount';
import FlatListItemCard from 'design-system/FlatListItemCard';
import ListEmptyView from 'design-system/ListEmptyView';
import Text from 'design-system/Text';
import useStyles from 'hooks/useStyles';
import ExtraMembersIntroCard from 'features/premium/components/ExtraMembersIntroCard';
import { selectDefaultCurrency } from 'reducers/selectors';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { stringSort } from 'utils/sort';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import useInviteFriends from '../hooks/useInviteFriends';
import ReferralsListItem from './ReferralsListItem';
const ReferralsStatusList = ({ data, status, total, currency, scrollY, width, contentContainerStyle, showExtraMembersCard = false, }) => {
    const styles = useStyles(styleSet);
    const defaultCurrency = useAppSelector(selectDefaultCurrency);
    const sortedData = useMemo(() => {
        const sorted = [...data].sort((a, b) => stringSort(status === 'COMPLETED' ? 'completedAt' : 'signedUpAt', a, b));
        return sorted;
    }, [data, status]);
    const renderHeader = useMemo(() => (<View>
        {sortedData.length > 0 ? (<FlatListItemCard style={styles.card} index={0}>
            <View style={styles.titleContainer}>
              <Text Text-16>Total</Text>
              <Amount {...(status === 'COMPLETED'
            ? { Blue: true }
            : { Secondary: true })} Numbers-16 value={{
                amount: total || 0,
                currency: currency || defaultCurrency,
            }}/>
            </View>
            <Separator />
          </FlatListItemCard>) : null}
      </View>), [
        sortedData.length,
        styles.card,
        styles.titleContainer,
        status,
        total,
        currency,
        defaultCurrency,
    ]);
    const renderFooter = useMemo(() => (<>
        {status === 'COMPLETED' ? (<CardView withPadding>
            <Text TextThin-14 Secondary>
              Credits are automatically deducted from your next subscription
              invoice.
            </Text>
          </CardView>) : null}
        {showExtraMembersCard ? (<>
            <SectionHeader horizontalMargins largeVerticalMargins>
              Add members into your plan
            </SectionHeader>
            <ExtraMembersIntroCard />
          </>) : null}
      </>), [status, showExtraMembersCard]);
    const renderItem = useCallback(({ item, index }) => (<FlatListItemCard style={styles.card} index={index + 1} itemsLength={sortedData.length + 1} onPress={() => {
            navigation.navigateDeprecated('CreditReferralDetails', {
                id: item.id,
            });
        }}>
        <ReferralsListItem item={item}/>
      </FlatListItemCard>), [sortedData.length, styles.card]);
    const navigation = useMainStackNavigation();
    const onPressInvite = useInviteFriends();
    const renderEmpty = useMemo(() => (<ListEmptyView compact onCard subTitlePadding={false} {...(status === 'COMPLETED'
        ? {
            title: 'No completed referrals yet',
            subTitle: 'You will see your earnings once the friends you invited start their free trial.',
        }
        : {
            title: 'No invites have been accepted yet',
            subTitle: 'You will see your pending invites once the people you invited create an account in Emma.',
        })}>
        <Button title="Invite & earn" small hug brandReversed onPress={onPressInvite}/>
      </ListEmptyView>), [onPressInvite, status]);
    const onScroll = useAnimatedScrollHandler((event) => {
        // eslint-disable-next-line no-param-reassign
        scrollY.value = event.contentOffset.y;
    }, []);
    return (<Animated.FlatList ListHeaderComponent={renderHeader} ListFooterComponent={renderFooter} ListEmptyComponent={renderEmpty} data={sortedData} renderItem={renderItem} onScroll={onScroll} contentContainerStyle={contentContainerStyle} pagingEnabled style={{ width }}/>);
};
const styleSet = createStyleSheets((colors) => ({
    titleContainer: {
        padding: rem(16),
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    buttonContainer: {
        flexDirection: 'row',
        paddingHorizontal: rem(16),
        marginBottom: rem(16),
        marginTop: rem(8),
    },
    card: {
        backgroundColor: colors.cards.onDark,
    },
}));
export default ReferralsStatusList;
